// ligfrontend/src/components/Lig/MyLigComponent.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { memberAPI, notesAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import ConfirmDialog from "../common/ConfirmDialog";
import LoadingSpinner from "../common/LoadingSpinner";
import NoteCard from "./NoteCard";
import MemberProfile from "./MemberProfile";
import PhotoUpload from "../common/PhotoUpload";
import "./MyLigComponent.css";

const MyLigComponent = () => {
  const navigate = useNavigate();
  const { uname } = useParams();
  const { user } = useAuth();
  const observerRef = useRef(null);
  const location = useLocation();
  const photoUploadKey = useRef(0); // Key to force PhotoUpload re-render

  const [notes, setNotes] = useState([]);
  const [member, setMember] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [newPhoto, setNewPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "danger",
  });

  const selectedYear = location.state?.selectedYear;

  useEffect(() => {
    loadNotes();
  }, [uname]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await notesAPI.getNotes();
      setNotes(data.notes || []);
      setMember(data.member);
    } catch (error) {
      console.error("Error loading notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotes = useCallback(async () => {
    if (loadingMore || !hasMore || notes.length === 0) return;

    try {
      setLoadingMore(true);
      const lastNote = notes[notes.length - 1];
      const data = await notesAPI.getMoreNotes(lastNote.date);

      if (data.notes && data.notes.length > 0) {
        setNotes((prev) => [...prev, ...data.notes]);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more notes:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [notes, loadingMore, hasMore]);

  const isNoteValid = () => {
    return newMsg && newMsg.trim().length > 5;
  };

  const handleNewPhotoSelect = async (file) => {
    setNewPhoto(file);
  };

  const handleNewPhotoRemove = () => {
    setNewPhoto(null);
  };

  const handleAddNote = async () => {
    if (!isNoteValid()) return;

    const newNote = {
      uname: user.uname,
      date: new Date().toISOString(),
      msg: newMsg.trim(),
    };

    try {
      setLoading(true);
      const data = await notesAPI.addNote(newNote);

      const savedNote = {
        ...newNote,
        _id: data.noteID,
      };

      // Upload photo if selected
      if (newPhoto) {
        const photoUrl = await uploadPhoto(data.noteID, newPhoto);
        savedNote.image = photoUrl;
      }
      console.log("Saved note", savedNote);

      setNotes((prev) => [savedNote, ...prev]);
      setMember((prev) => ({
        ...prev,
        yearStats: {
          ...prev.yearStats,
          [selectedYear]: data.member.yearStats[selectedYear],
        },
      }));

      // Clear form and force PhotoUpload to reset
      setNewMsg("");
      setNewPhoto(null);
      photoUploadKey.current += 1; // Increment key to force re-render
    } catch (error) {
      console.error("Error adding note:", error);
      setConfirmDialog({
        isOpen: true,
        title: "Error",
        message: "Failed to save note. Please try again.",
        type: "danger",
        onConfirm: closeConfirmDialog,
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (noteId, file) => {
    try {
      console.log("Uploading photo for note", noteId, file);
      const response = await notesAPI.uploadPhoto(noteId, file);
      return response.url;
    } catch (error) {
      console.error("Error uploading photo:", error);
      throw error;
    }
  };

  const handleUpdateNotePhoto = async (noteId, file) => {
    try {
      let image = null;

      if (file) {
        image = await uploadPhoto(noteId, file);
      } else {
        // Remove photo
        await notesAPI.removePhoto(noteId);
      }

      setNotes((prev) =>
        prev.map((n) => (n._id === noteId ? { ...n, image } : n))
      );
    } catch (error) {
      console.error("Error updating note photo:", error);
    }
  };

  const handleDeleteNote = (noteId) => {
    const note = notes.find((n) => n._id === noteId);
    if (!note) return;

    setConfirmDialog({
      isOpen: true,
      title: "Delete Note",
      message: "The note will be permanently removed. Are you sure?",
      type: "danger",
      onConfirm: () => confirmDeleteNote(note),
    });
  };

  const confirmDeleteNote = async (note) => {
    closeConfirmDialog();

    try {
      setLoading(true);
      const data = await notesAPI.deleteNote(note._id, note.uname, note.date);
      setNotes((prev) => prev.filter((n) => n._id !== note._id));
      setMember((prev) => ({
        ...prev,
        yearStats: {
          ...prev.yearStats,
          [selectedYear]: data.yearStats[selectedYear],
        },
      }));
    } catch (error) {
      console.error("Error deleting note:", error);
      setConfirmDialog({
        isOpen: true,
        title: "Error",
        message: "Failed to delete note. Please try again.",
        type: "danger",
        onConfirm: closeConfirmDialog,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNote = async (noteId, newMessage) => {
    const note = notes.find((n) => n._id === noteId);
    if (!note || note.msg === newMessage) return;

    const updatedNote = { ...note, msg: newMessage };

    try {
      await notesAPI.updateNote(updatedNote);
      setNotes((prev) => prev.map((n) => (n._id === noteId ? updatedNote : n)));
    } catch (error) {
      console.error("Error updating note:", error);
    }
  };

  const handleUpdateNoteDate = async (noteId, newDate) => {
    const note = notes.find((n) => n._id === noteId);
    if (!note) return;

    const oldDate = new Date(note.date);
    const updatedDate = new Date(newDate);

    updatedDate.setHours(oldDate.getHours());
    updatedDate.setMinutes(oldDate.getMinutes());
    updatedDate.setSeconds(oldDate.getSeconds());

    const updatedNote = { ...note, date: updatedDate.toISOString() };

    try {
      await notesAPI.updateNote(updatedNote);
      setNotes((prev) => prev.map((n) => (n._id === noteId ? updatedNote : n)));
    } catch (error) {
      console.error("Error updating note date:", error);
    }
  };

  const handleUpdateMember = async (updates) => {
    if (!member) return;

    const updatedMember = { ...member, ...updates };

    try {
      const data = await memberAPI.updateMember(updatedMember);
      setMember(data);
    } catch (error) {
      console.error("Error updating member:", error);
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "danger",
    });
  };

  const lastNoteRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreNotes();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loadingMore, hasMore, loadMoreNotes]
  );

  if (loading && notes.length === 0) {
    return <LoadingSpinner message="Loading your notes..." />;
  }

  return (
    <>
      <div className="mylig-container">
        <div className="mylig-header">
          <button className="back-button" onClick={() => navigate("/lig")}>
            ← My Family
          </button>
        </div>

        {member && (
          <MemberProfile
            member={member}
            selectedYear={selectedYear}
            onUpdateName={(name) => handleUpdateMember({ name })}
            onUpdateAvatar={(avatar) => handleUpdateMember({ avatar })}
          />
        )}

        <div className="note-input-section">
          <textarea
            className="note-input"
            rows="4"
            placeholder="Write something memorable..."
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
          />

          <PhotoUpload
            key={photoUploadKey.current} // Force re-render when key changes
            onPhotoSelect={handleNewPhotoSelect}
            onPhotoRemove={handleNewPhotoRemove}
          />

          <button
            className="btn-save-note"
            disabled={!isNoteValid()}
            onClick={handleAddNote}
          >
            Save Note
          </button>
        </div>

        <div className="notes-list">
          {notes.map((note, index) => (
            <div
              key={note._id}
              ref={index === notes.length - 1 ? lastNoteRef : null}
            >
              <NoteCard
                note={note}
                onUpdateMessage={handleUpdateNote}
                onUpdateDate={handleUpdateNoteDate}
                onUpdatePhoto={handleUpdateNotePhoto}
                onDelete={handleDeleteNote}
              />
            </div>
          ))}

          {loadingMore && (
            <div className="loading-more">
              <div className="spinner-small"></div>
              <span>Loading more notes...</span>
            </div>
          )}

          {notes.length === 0 && (
            <div className="empty-notes">
              <div className="empty-notes-icon">📝</div>
              <h3>No Notes Yet</h3>
              <p>Start documenting your good moments!</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm || closeConfirmDialog}
        onCancel={closeConfirmDialog}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default MyLigComponent;
